import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../../../firebase/firebase.config';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { AlertCircle, Loader2, Lock } from 'lucide-react';

const SecuritySection = ({
    isMobile,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword
}) => {
    const [isPasswordUser, setIsPasswordUser] = useState(false);
    const [providerChecked, setProviderChecked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showProviderModal, setShowProviderModal] = useState(false);

    // Detect auth providers from Firebase (source of truth)
    useEffect(() => {
        const providers = auth.currentUser?.providerData || [];
        const hasPassword = providers.some((p) => p.providerId === 'password');
        setIsPasswordUser(hasPassword);
        setProviderChecked(true);
    }, []);

    const inputsDisabled = useMemo(() => !providerChecked || !isPasswordUser || saving, [providerChecked, isPasswordUser, saving]);

    const handleSubmit = async () => {
        setError('');
        setSuccess('');

        // Block Google-only accounts
        if (!isPasswordUser) {
            setShowProviderModal(true);
            return;
        }

        if (!currentPassword || !newPassword) {
            setError('Please enter both current and new password.');
            return;
        }
        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters.');
            return;
        }
        if (currentPassword === newPassword) {
            setError('New password must be different from current password.');
            return;
        }

        const user = auth.currentUser;
        if (!user?.email) {
            setError('Unable to change password for this account.');
            return;
        }

        setSaving(true);
        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            setSuccess('Password updated successfully.');
            setCurrentPassword('');
            setNewPassword('');
        } catch (err) {
            switch (err?.code) {
                case 'auth/wrong-password':
                    setError('Current password is incorrect.');
                    break;
                case 'auth/too-many-requests':
                    setError('Too many attempts. Please wait and try again.');
                    break;
                case 'auth/requires-recent-login':
                    setError('Please sign in again to change your password.');
                    break;
                default:
                    setError('Could not update password. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="mb-4 md:mb-6">
                <h2 className="text-lg font-bold text-notion-text">Security</h2>
                <p className="text-xs text-stone-500">{isMobile ? 'Account protection.' : 'Protect your account.'}</p>
            </div>

            <div className="bg-white border border-notion-border rounded-xl p-5 md:p-6 shadow-card">
                <div className="flex items-start justify-between mb-3 md:mb-4">
                    <h3 className="text-sm font-semibold text-notion-text">Change Password</h3>
                    {!isPasswordUser && providerChecked && (
                        <span className="text-[10px] text-stone-500">Uses Google Sign-In</span>
                    )}
                </div>

                {!isPasswordUser && providerChecked && (
                    <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                        <p className="text-xs text-stone-700">This account is signed in with Google. Password changes are managed in your Google Account.</p>
                    </div>
                )}

                <div className="space-y-3">
                    <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Current Password"
                        disabled={inputsDisabled}
                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition ${inputsDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New Password (min 8 chars)"
                        disabled={inputsDisabled}
                        className={`w-full bg-notion-sidebar border-transparent rounded px-3 ${isMobile ? 'py-2.5 text-sm' : 'py-2 text-xs'} outline-none focus:border-stone-300 border transition ${inputsDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />

                    {error && (
                        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-200 rounded px-3 py-2">
                            <AlertCircle className="w-4 h-4 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {success && (
                        <div className="flex items-start gap-2 text-green-700 text-xs bg-green-50 border border-green-200 rounded px-3 py-2">
                            <Lock className="w-4 h-4 mt-0.5" />
                            <span>{success}</span>
                        </div>
                    )}

                    <button
                        onClick={handleSubmit}
                        disabled={inputsDisabled}
                        className={`${isMobile ? 'w-full' : ''} bg-stone-800 text-white px-4 ${isMobile ? 'py-2.5' : 'py-2'} rounded ${isMobile ? 'text-sm' : 'text-xs'} font-medium hover:bg-stone-700 disabled:opacity-50 flex items-center justify-center gap-2 transition`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {isMobile ? 'Update' : 'Update Password'}
                    </button>
                </div>
            </div>

            {/* Provider restriction modal */}
            {showProviderModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-popover border border-notion-border w-[90%] max-w-sm p-6 space-y-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-semibold text-notion-text">Password Change Not Available</h3>
                                <p className="text-xs text-stone-600 mt-1">
                                    This account is signed in using Google. To change your password, please update it in your Google Account settings.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowProviderModal(false)}
                                className="px-4 py-2 bg-stone-800 text-white text-xs font-medium rounded hover:bg-stone-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecuritySection;
